import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';

import {hideStepDatasSection} from '../AnalysisFlowchartNode';
import {ANALYSIS_FLOWCHART_NODE_WIDTH} from '../AnalysisFlowchartStyleConstants';
import {AnalysisGraph, AnalysisGraphNode} from './AnalysisGraphTypes';

/**
 * The minimum amount of space afforded a node in the layout.
 *
 * A node with a two-line title and a two-line status message will consume about this amount of space; nodes
 * with shorter titles or no status messages will be smaller.
 *
 * Include the height for a status message for all nodes, so the flowchart doesn't rearrange as the pipeline
 * executes and statuses change.
 */
const MIN_NODE_HEIGHT = 95;

/**
 * Estimate a node's expected height, for layout purposes.
 */
function calculateNodeHeight(node: AnalysisGraphNode): number {
  // For single-input/single-output steps, input/output step datas are not individually listed
  const numRows =
    (hideStepDatasSection(node.data.step, 'INPUT') ? 0 : node.data.step.inputStepDatas.length + 1) +
    (hideStepDatasSection(node.data.step, 'OUTPUT') ? 0 : node.data.step.outputStepDatas.length + 1);

  return MIN_NODE_HEIGHT + numRows * 20;
}

/**
 * Compute positions to arrange the flowchart graph of an analysis.
 *
 * @param unarrangedGraph An AnalysisFlowchart with uncomputed `position` values on the nodes
 * @returns An AnalysisFlowchart with updated `position` values on the nodes, to ensure an attractive layout
 */
export default async function arrangeAnalysisGraph(unarrangedGraph: AnalysisGraph): Promise<AnalysisGraph> {
  return await layoutFlowchart(unarrangedGraph, {
    // Calculate custom node dimensions, so that nodes don't change dimensions as the pipeline executes, and
    // step statuses change. This calculation also doesn't require `reactflow` to have calculated nodes'
    // dimensions, which simplifies state management. See `useAutoLayout`.
    calculateNodeDimensions: (node) => ({
      height: calculateNodeHeight(node),
      width: ANALYSIS_FLOWCHART_NODE_WIDTH * 0.8,
    }),
    // Balance (a) providing sufficient space to drag-and-drop edges with (b) fitting more nodes on the screen
    horizontalSpaceBetweenNodes: ANALYSIS_FLOWCHART_NODE_WIDTH,
  });
}
